import { type ProspectDto } from "@crowdcoursing/api/openapi";
import { AccessTime, Language, LocationOn, Source } from "@mui/icons-material";
import { Avatar, Box, FormControl, InputLabel, MenuItem, Paper, Select, Typography } from "@mui/material";

import { US_TIMEZONES } from "~constants/timezones";
import { AssignmentsCard } from "~ui/molecules/AssignmentsCard/AssignmentsCard";
import { EditableField } from "~ui/molecules/EditableField/EditableField";
import { LinkifiedEditableField } from "~ui/molecules/EditableField/LinkifiedEditableField";

const tAssignments = "Assignments";
const tBasicInformation = "Basic Information";
const tWebsite = "Website";
const tRegion = "Region";
const tSource = "Source";
const tTimeZone = "Time Zone";
const tSelectTimeZone = "Select time zone";

interface OverviewTabProps {
  prospect: ProspectDto;
  onAssignToMe: () => Promise<void>;
  onUnassignMe: () => Promise<void>;
  onFieldUpdate: (field: "region" | "source" | "website" | "timeZone", value: string) => Promise<void>;
  roles?: string[];
  onAssignUser?: (userId: string) => Promise<void>;
  onUnassignUser?: (userId: string) => Promise<void>;
}

export const OverviewTab = ({
  prospect,
  onAssignToMe,
  onUnassignMe,
  onFieldUpdate,
  roles = [],
  onAssignUser,
  onUnassignUser,
}: OverviewTabProps) => {
  return (
    <Box
      display="grid"
      gap={3}
      sx={{
        gridTemplateColumns: {
          md: "1fr 1fr", // Two equal columns on medium screens and up
          xs: "1fr", // Single column on small screens
        },
      }}
    >
      {/* Basic Info */}
      <Paper sx={{ p: 3 }} component="section" aria-labelledby="basic-info-heading" role="region">
        <Typography variant="h6" gutterBottom id="basic-info-heading">
          {tBasicInformation}
        </Typography>
        <Box display="grid" gap={2} component="div" role="group" aria-label="Basic information fields">
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {prospect.website ? (
                <Avatar
                  src={`https://www.google.com/s2/favicons?domain=${prospect.website}&sz=32`}
                  sx={{ height: 24, width: 24 }}
                  alt={`Favicon for ${prospect.website}`}
                >
                  <Language fontSize="small" color="primary" aria-hidden="true" />
                </Avatar>
              ) : (
                <Language fontSize="small" color="primary" aria-hidden="true" data-testid="language-icon" />
              )}
              <Typography variant="subtitle2" color="text.secondary">
                {tWebsite}
              </Typography>
            </Box>
            <LinkifiedEditableField
              maxLength={300}
              value={prospect.website}
              onUpdate={(value) => onFieldUpdate("website", value)}
            />
          </Box>

          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <LocationOn fontSize="small" sx={{ color: "info.main" }} aria-hidden="true" data-testid="location-icon" />
              <Typography variant="subtitle2" color="text.secondary">
                {tRegion}
              </Typography>
            </Box>
            <EditableField
              maxLength={120}
              value={prospect.region}
              onUpdate={(value) => onFieldUpdate("region", value)}
            />
          </Box>

          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Source fontSize="small" sx={{ color: "warning.main" }} aria-hidden="true" data-testid="source-icon" />
              <Typography variant="subtitle2" color="text.secondary">
                {tSource}
              </Typography>
            </Box>
            <EditableField
              maxLength={100}
              value={prospect.source}
              onUpdate={(value) => onFieldUpdate("source", value)}
            />
          </Box>

          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <AccessTime
                fontSize="small"
                sx={{ color: "secondary.main" }}
                aria-hidden="true"
                data-testid="timezone-icon"
              />
              <Typography variant="subtitle2" color="text.secondary">
                {tTimeZone}
              </Typography>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel id="prospect-timezone-label">{tSelectTimeZone}</InputLabel>
              <Select
                labelId="prospect-timezone-label"
                id="prospect-timezone"
                value={prospect.timeZone ?? ""}
                label={tSelectTimeZone}
                onChange={(e) => onFieldUpdate("timeZone", e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {US_TIMEZONES.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Assignments */}
      <Paper sx={{ p: 3 }} component="section" aria-labelledby="assignments-heading" role="region">
        <Typography variant="h6" gutterBottom id="assignments-heading">
          {tAssignments}
        </Typography>
        <AssignmentsCard
          prospect={prospect}
          onAssignToMe={onAssignToMe}
          onUnassignMe={onUnassignMe}
          roles={roles}
          onAssignUser={onAssignUser}
          onUnassignUser={onUnassignUser}
        />
      </Paper>
    </Box>
  );
};
